import { Injectable, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '../../core/services/auth/auth.service';
import { RequestDetailPatient } from '../models/request-detail';

export interface PatientRecordsSearchHandoff {
  patientPortalUserRequestId: number;
  patients: RequestDetailPatient[];
  selectedPatientId: number;
  /**
   * Server-side total for the search that produced `patients`, so the detail
   * screen can keep saying "showing 25 of 60" instead of reverting to
   * "25 results" after the handoff. `null` when the total isn't known.
   */
  totalRecordCount: number | null;
  /**
   * Open the Confirm & Link review on arrival, rather than landing on the
   * detail screen for staff to find the button again. Set by Select & Review;
   * the modal picks its own state (consistent match, or identity mismatch with
   * a required override justification) from the record that was selected.
   */
  openReview: boolean;
}

/**
 * Carries the staff member's Search Patient Records results back to the
 * Request Detail screen (Select & Review). Root-provided so it survives the
 * navigation between the two screens; the detail screen consumes (and clears)
 * the handoff on load so a later plain visit starts from the auto-search
 * results again.
 */
@Injectable({
  providedIn: 'root',
})
export class PatientRecordsSearchStateService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  private readonly handoff = signal<PatientRecordsSearchHandoff | null>(null);

  constructor() {
    // PHI hygiene: the handoff holds patient records in memory. If it is
    // never consumed (detail load failed, staff navigated elsewhere), don't
    // let it outlive the sign-in that fetched it.
    effect(() => {
      if (!this.authService.currentUser()) {
        this.handoff.set(null);
      }
    });

    // Second PHI backstop: the handoff exists only to survive the hop from the
    // search screen to its own request's detail screen. Landing anywhere else
    // means it will never be consumed, so drop it there and then rather than
    // letting patient records sit in this root singleton until sign-out. The
    // service is root-provided and never destroyed, so this subscription lives
    // for the app's lifetime by design.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.clearIfNavigatedAway(event.urlAfterRedirects));
  }

  setHandoff(handoff: PatientRecordsSearchHandoff): void {
    this.handoff.set(handoff);
  }

  /**
   * Returns and clears the pending handoff when it belongs to the given
   * request. Callers should consume it before they know whether the rest of
   * their load succeeded — clearing is the point, so PHI doesn't linger in
   * this singleton when the caller bails out.
   */
  consumeHandoff(patientPortalUserRequestId: number): PatientRecordsSearchHandoff | null {
    const pending = this.handoff();
    if (!pending || pending.patientPortalUserRequestId !== patientPortalUserRequestId) {
      return null;
    }
    this.handoff.set(null);
    return pending;
  }

  /**
   * Drop the pending handoff unless the navigation landed on its own request's
   * detail screen (or a child of it, such as the search screen that set it).
   * Scoped to the owning request id, not just the route shape — a handoff for
   * request 1 has no business surviving a trip to request 2's detail screen,
   * where `consumeHandoff` would refuse it anyway.
   */
  private clearIfNavigatedAway(url: string): void {
    const pending = this.handoff();
    if (!pending) {
      return;
    }

    const detailRoot = `/request-detail/${pending.patientPortalUserRequestId}`;
    const path = url.split('?')[0].split('#')[0];
    if (path === detailRoot || path.startsWith(`${detailRoot}/`)) {
      return;
    }

    this.handoff.set(null);
  }
}
